import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { HelpCircle, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useDepartmentsQuery } from "@/modules/departments/presentation/api/queries";
import { QueryState } from "@/shared/ui/QueryState";
import type { KPIPeriodFilter as KPIPeriodFilterType } from "../../domain/types";
import { useKPIOverview } from "../api/queries";
import { KPIOverviewTour, useKPIOverviewTour } from "../tour/KPIOverviewTour";
import { KPIPeriodFilter } from "../ui/KPIPeriodFilter";
import { KPISummaryCards } from "../ui/KPISummaryCards";

export function KPIOverviewPage() {
  const tour = useKPIOverviewTour();
  const now = new Date();
  const [filter, setFilter] = useState<KPIPeriodFilterType>({
    periodType: "monthly",
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    departmentId: "",
    search: "",
  });

  const { data: deptResponse } = useDepartmentsQuery({ limit: 200 });
  const departments = deptResponse?.data ?? [];
  const { data, isLoading, error } = useKPIOverview(filter);

  // Format period display text
  const periodText = useMemo(() => {
    if (filter.periodType === "monthly") {
      const monthNames = [
        "ມັງກອນ",
        "ກຸມພາ",
        "ມີນາ",
        "ເມສາ",
        "ພຶດສະພາ",
        "ມິຖຸນາ",
        "ກໍລະກົດ",
        "ສິງຫາ",
        "ກັນຍາ",
        "ຕຸລາ",
        "ພະຈິກ",
        "ທັນວາ",
      ];
      const monthName = filter.month
        ? monthNames[filter.month - 1]
        : monthNames[0];
      return `${monthName} ${filter.year}`;
    }
    return `ໄຕມາດ ${filter.quarter}/${filter.year}`;
  }, [filter]);

  if (isLoading) {
    return <QueryState isLoading>ກຳລັງໂຫຼດຂໍ້ມູນ KPI...</QueryState>;
  }

  if (error) {
    return (
      <QueryState isError error={error}>
        ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ KPI
      </QueryState>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <KPIOverviewTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />

        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="font-bold text-2xl tracking-tight">ພາບລວມ KPI</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground text-sm">
              ພາບລວມ KPI - {periodText}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <KPIPeriodFilter value={filter} onChange={setFilter} />
            <Select
              value={filter.departmentId || "all"}
              onValueChange={(v) =>
                setFilter((prev) => ({
                  ...prev,
                  departmentId: v === "all" ? "" : v,
                }))
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="ພະແນກ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ທຸກພະແນກ</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-[200px]">
              <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="ຄົ້ນຫາຊື່ KPI"
                className="pl-9"
                value={filter.search ?? ""}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, search: e.target.value }))
                }
              />
            </div>
          </div>
        </div>

        {data && <KPISummaryCards data={data} filter={filter} />}
      </Main>
    </>
  );
}
